import React, { useState, useEffect } from 'react';
import { getApiErrorMessage, getApiErrorMessageAsync } from '../../utils/mapFieldErrors';
import { useParams, useNavigate } from 'react-router-dom';
import { parseRouteId } from '../../utils/routeId';
import Layout from '../../components/ui/Layout';
import {
  RiSaveLine,
  RiArrowLeftLine,
  RiFileTextLine,
  RiCalendarLine,
  RiBuildingLine,
  RiMapPinLine,
  RiUserLine,
  RiTruckLine,
  RiShoppingCartLine,
  RiInformationLine,
  RiEditLine,
  RiCloseLine,
  RiListCheck,
  RiEyeLine,
  RiAttachmentLine,
  RiDeleteBinLine,
  RiUploadLine,
  RiMoneyDollarCircleLine
} from 'react-icons/ri';
import contractService, { Contract as ContractFull } from '../../services/contractService';
import AddendumFormModal from '../../components/contract/AddendumFormModal';
import companyService from '../../services/companyService';
import { getStatusClasses } from '../../config/designSystem';
import rentalService, { Rental, RentalStatus } from '../../services/rentalService';
import authorizedSignerService, { AuthorizedSigner } from '../../services/authorizedSignerService';
import { showAlert, showConfirm } from '../../utils/alert';
import SearchableSelect from '../../components/ui/SearchableSelect';
import ApiWithLogging from '../../utils/apiWithLogging';
import DualScrollWrapper from '../../components/ui/DualScrollWrapper';
import { usePermission } from '../../hooks/usePermission';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import ConfirmModal from '../../components/ui/ConfirmModal';
import LoadingOverlayModal from '../../components/ui/LoadingOverlayModal';

// ประเภทข้อมูลสัญญา
interface Contract {
  id: number;
  contract_number: string;
  company_id: number;
  start_date: string;
  end_date: string;
  status: string;
  notes: string;
}

// ข้อมูลสำหรับ dropdown
interface DropdownOption {
  id: number;
  name: string;
}

// ข้อมูลฟอร์ม
interface FormData {
  contract_number: string;
  company_id: number | '';
  start_date: string;
  end_date: string;
  status: string;
  terms_conditions: string;
  notes: string;
  amendment_reason?: string; // เหตุผลการแก้ไข (สำหรับสัญญา ACTIVE)
  /** FB-2026-0004: วันที่ทำสัญญา (สัญญาหลัก) */
  contract_date?: string;
}

const ContractEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  // BUG-S2283-001: id ต้องเป็นจำนวนเต็มบวกล้วน ("487abc"/"1e3" เดิมเปิดสัญญาผิดตัว) · null = ไม่ถูกต้อง/โหมดสร้าง
  const recordId = parseRouteId(id);
  const navigate = useNavigate();
  const { canEdit, canCreate } = usePermission();

  // State
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [loadingRentals, setLoadingRentals] = useState(false);

  // ข้อมูลฟอร์ม
  const [formData, setFormData] = useState<FormData>({
    contract_number: '',
    company_id: '',
    start_date: '',
    end_date: '',
    status: 'DRAFT',  // สัญญาใหม่เริ่มที่ร่างเสมอ — อนุมัติแล้วจึงเป็น ACTIVE
    terms_conditions: '',
    notes: '',
    amendment_reason: ''
  });

  // ตรวจสอบว่าเป็นสัญญา ACTIVE หรือไม่
  const [isActiveContract, setIsActiveContract] = useState(false);
  // FB-2026-0004/0006: ประเภทสัญญา — 'MAIN' แก้วันที่ทำสัญญาแทนวันเริ่ม/สิ้นสุด · 'ADDENDUM' แก้ผ่านโมดัล
  const [contractKind, setContractKind] = useState<'MAIN' | 'ADDENDUM' | null>(null);
  const [addendumForModal, setAddendumForModal] = useState<ContractFull | null>(null);
  const [showAddendumModal, setShowAddendumModal] = useState(false);
  const isMain = contractKind === 'MAIN';
  // S1230: capture updated_at from initial load for opt-in optimistic-concurrency
  const [contractUpdatedAt, setContractUpdatedAt] = useState<string | undefined>(undefined);

  // ข้อมูลสำหรับ dropdown
  const [companies, setCompanies] = useState<DropdownOption[]>([]);
  
  // รายการเช่าทั้งหมดของบริษัท
  const [companyRentals, setCompanyRentals] = useState<Rental[]>([]);
  // รายการ ID ของการเช่าที่เลือก
  const [selectedRentalIds, setSelectedRentalIds] = useState<number[]>([]);
  
  // การแนบไฟล์
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [existingDocuments, setExistingDocuments] = useState<any[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [downloadingDocId, setDownloadingDocId] = useState<number | null>(null);

  // ผู้มีอำนาจลงนาม
  const [availableSigners, setAvailableSigners] = useState<AuthorizedSigner[]>([]);
  // KD3 S2122 (BUG-S2122-002): ผู้ลงนามเดิมของสัญญาที่ไม่อยู่ในรายชื่อที่ใช้งานแล้ว (ถูกลบ/ปิดใช้งาน) — ต้องเห็นเพื่อเอาออกได้
  const [existingSignerSnapshots, setExistingSignerSnapshots] = useState<{ id: number; name: string; position: string }[]>([]);
  const [selectedSignerIds, setSelectedSignerIds] = useState<number[]>([]);
  const [minRequiredSigners, setMinRequiredSigners] = useState(1);

  // Unsaved changes warning
  const { showModal, onConfirmLeave, onCancelLeave } = useUnsavedChanges(isFormDirty);
  const [showBackConfirm, setShowBackConfirm] = useState(false);
  const handleBack = () => {
    if (isFormDirty) {
      setShowBackConfirm(true);
    } else {
      navigate('/contracts');
    }
  };

  // สถานะสัญญาแสดงอย่างเดียว — เปลี่ยนได้ผ่านปุ่ม อนุมัติ/ปฏิเสธ/ยกเลิกสัญญา เท่านั้น
  const statusBadge = getStatusClasses('contract', formData.status);

  // NaN guard: ตรวจสอบ id ว่าเป็นตัวเลขที่ถูกต้อง
  useEffect(() => {
    if (id && recordId === null) {
      navigate('/contracts', { replace: true });
    }
  }, [id, recordId, navigate]);

  // ตรวจสอบ permission
  useEffect(() => {
    if (id && !canEdit('contracts')) {
      showAlert('ไม่มีสิทธิ์เข้าถึง', 'คุณไม่มีสิทธิ์ในการแก้ไขสัญญา', 'error');
      navigate('/contracts');
    } else if (!id && !canCreate('contracts')) {
      showAlert('ไม่มีสิทธิ์เข้าถึง', 'คุณไม่มีสิทธิ์ในการสร้างสัญญา', 'error');
      navigate('/contracts');
    }
  }, [id, canEdit, canCreate, navigate]);

  // โหลดข้อมูลเริ่มต้น
  useEffect(() => {
    if (id) {
      // BUG-S2283-001: id ไม่ถูกต้อง → ไม่ยิง API (effect ด้านบน redirect ไปหน้ารายการ)
      if (recordId === null) return;
      loadInitialData();
      loadContractData();
    } else {
      loadInitialData();
    }
  }, [id]);

  // โหลดรายการเช่าและ signers เมื่อเปลี่ยนบริษัท
  useEffect(() => {
    if (formData.company_id) {
      loadCompanyRentals(Number(formData.company_id));
      // โหลด signers เมื่อเปลี่ยนบริษัท (เฉพาะกรณีที่ไม่ได้กำลัง load contract data อยู่)
      if (!id) {
        loadSignersByCompany(Number(formData.company_id));
      }
    } else {
      setCompanyRentals([]);
      setSelectedRentalIds([]);
      setAvailableSigners([]);
      setSelectedSignerIds([]);
    }
  }, [formData.company_id]);

  const loadInitialData = async () => {
    try {
      // โหลดข้อมูลบริษัท
      const companiesResponse = await companyService.getCompanies({ page: 1, limit: 1000 });
      setCompanies(companiesResponse.items.map(company => ({
        id: company.id,
        name: company.name
      })));

    } catch (error) {
      console.error('Error loading initial data:', error);
      showAlert('เกิดข้อผิดพลาด', 'ไม่สามารถโหลดข้อมูลเริ่มต้นได้', 'error');
    }
  };

  const loadContractData = async () => {
    if (recordId === null) return;
    
    try {
      setLoading(true);
      const contract = await contractService.getContract(recordId);

      // S1230: store updated_at for opt-in If-Match on save (OBS-S1223-001 stack)
      // BUG-S216-IFMATCH-NULL-CT: สัญญาที่ยังไม่เคยถูกแก้มี updated_at = null → เดิมไม่ส่ง
      // If-Match เลย = optimistic lock ปิดตัวเองเงียบๆ · fallback ไป created_at (มีทุกแถว ไม่เปลี่ยน)
      setContractUpdatedAt(contract.updated_at || (contract as any).created_at);

      setFormData({
        contract_number: contract.contract_number,
        company_id: contract.company_id,
        start_date: contract.start_date?.split('T')[0] || '', // Format เป็น YYYY-MM-DD
        end_date: contract.end_date?.split('T')[0] || '',
        status: contract.status,
        terms_conditions: contract.terms_conditions || '',
        notes: contract.notes || '',
        amendment_reason: '',
        contract_date: (contract.contract_date || contract.start_date || '').split('T')[0],
      });
      const kind = contract.contract_type === 'MAIN' || contract.contract_type === 'ADDENDUM' ? contract.contract_type : null;
      setContractKind(kind);
      if (kind === 'ADDENDUM') setAddendumForModal(contract);

      // ตรวจสอบสถานะสัญญา
      setIsActiveContract(contract.status === 'ACTIVE');

      // โหลดรายการเช่าที่อยู่ในสัญญานี้
      const rentalsResponse = await rentalService.getRentals({ 
        contract_id: recordId,
        limit: 100 
      });
      
      // เก็บ IDs ของรายการเช่าที่อยู่ในสัญญา
      const rentalIds = rentalsResponse.items.map(rental => rental.id);
      setSelectedRentalIds(rentalIds);
      
      // โหลดเอกสารที่มีอยู่ของสัญญา (ถ้ามี)
      // อาจจะไม่มีเอกสารก็ได้ ไม่ต้อง error
      try {
        await loadExistingDocuments(recordId);
      } catch (error) {
      }

      // โหลด signers ของบริษัท และ pre-select จาก contract_signers ที่มีอยู่
      try {
        const signers = await authorizedSignerService.getActiveSignersByCompany(contract.company_id);
        setAvailableSigners(signers);

        // Pre-select จาก contract_signers ที่บันทึกไว้
        if (contract.contract_signers && contract.contract_signers.length > 0) {
          // authorized_signer_id เป็น NULL ได้ (ผู้ลงนามถูกลบแบบ hard) — ห้ามส่ง null กลับไป
          const existing = contract.contract_signers.filter(cs => cs.authorized_signer_id != null);
          setSelectedSignerIds(existing.map(cs => cs.authorized_signer_id));
          setExistingSignerSnapshots(existing.map(cs => ({
            id: cs.authorized_signer_id,
            name: cs.signer_full_name,
            position: cs.signer_position,
          })));
        } else {
          // ถ้าไม่มี contract_signers เดิม auto-select primary signers
          const primaryIds = signers.filter(s => s.is_primary).map(s => s.id);
          setSelectedSignerIds(primaryIds);
        }
      } catch (error) {
        console.error('Error loading authorized signers:', error);
      }

    } catch (error) {
      console.error('Error loading contract:', error);
      showAlert('เกิดข้อผิดพลาด', 'ไม่สามารถโหลดข้อมูลสัญญาได้', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadSignersByCompany = async (companyId: number) => {
    try {
      const signers = await authorizedSignerService.getActiveSignersByCompany(companyId);
      setAvailableSigners(signers);
      // Reset signer selection เมื่อเปลี่ยนบริษัท — auto-select primary
      const primaryIds = signers.filter(s => s.is_primary).map(s => s.id);
      setSelectedSignerIds(primaryIds);

      // ดึง min_required_signers จาก companies list
      const company = companies.find(c => c.id === companyId);
      if (company) {
        const minRequired = (company as any).min_required_signers ?? (company as any).minRequiredSigners ?? 1;
        setMinRequiredSigners(minRequired);
      }
    } catch (error) {
      console.error('Error loading signers:', error);
    }
  };

  const loadCompanyRentals = async (companyId: number) => {
    try {
      setLoadingRentals(true);
      
      // ถ้าเป็นการแก้ไขสัญญา ต้องโหลดรายการเช่าที่อยู่ในสัญญานี้ด้วย
      if (id && recordId !== null) {
        // โหลด 2 ชุด: 1. รายการที่อยู่ในสัญญานี้ 2. รายการที่พร้อมเพิ่มใหม่
        const [currentContractRentals, availableRentals] = await Promise.all([
          // รายการที่อยู่ในสัญญานี้
          rentalService.getRentals({ 
            contract_id: recordId,
            limit: 100 
          }),
          // รายการที่พร้อมเพิ่มในสัญญา
          rentalService.getRentals({ 
            company_id: companyId,
            status: RentalStatus.ACTIVE,
            available_for_contract: true,
            limit: 100 
          })
        ]);
        
        // รวมรายการทั้งสองชุด (ลบรายการซ้ำ)
        const allRentals = [...currentContractRentals.items];
        const existingIds = new Set(allRentals.map(r => r.id));
        
        // เพิ่มเฉพาะรายการที่ยังไม่มี
        availableRentals.items.forEach(rental => {
          if (!existingIds.has(rental.id)) {
            allRentals.push(rental);
          }
        });
        
        setCompanyRentals(allRentals);
      } else {
        // ถ้าเป็นการสร้างสัญญาใหม่ โหลดเฉพาะรายการที่พร้อมใช้
        const response = await rentalService.getRentals({ 
          company_id: companyId,
          status: RentalStatus.ACTIVE,
          available_for_contract: true,
          limit: 100 
        });
        setCompanyRentals(response.items);
      }
    } catch (error) {
      console.error('Error loading company rentals:', error);
      setCompanyRentals([]);
    } finally {
      setLoadingRentals(false);
    }
  };

  // จัดการการเลือก/ไม่เลือกรายการเช่า
  const handleRentalToggle = (rentalId: number) => {
    setSelectedRentalIds(prev => {
      if (prev.includes(rentalId)) {
        return prev.filter(id => id !== rentalId);
      } else {
        return [...prev, rentalId];
      }
    });
  };

  // จัดการการเปลี่ยนแปลงข้อมูลฟอร์ม
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    let processedValue: string | number = value;
    
    if (type === 'number' && value !== '') {
      processedValue = parseFloat(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
    
    // ล้าง error เมื่อผู้ใช้เริ่มพิมพ์
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // ตรวจสอบความถูกต้องของข้อมูล
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.contract_number.trim()) {
      errors.contract_number = 'กรุณากรอกเลขที่สัญญา';
    }

    if (!formData.company_id) {
      errors.company_id = 'กรุณาเลือกบริษัท';
    }

    if (isMain) {
      // FB-2026-0004: สัญญาหลัก = สัญญาแบบเปิด ใส่แค่วันที่ทำสัญญา
      if (!formData.contract_date) {
        errors.contract_date = 'กรุณาระบุวันที่ทำสัญญา';
      }
    } else {
      if (!formData.start_date) {
        errors.start_date = 'กรุณาเลือกวันที่เริ่มต้นสัญญา';
      }

      if (!formData.end_date) {
        errors.end_date = 'กรุณาเลือกวันที่สิ้นสุดสัญญา';
      }

      // R204: allow same-day (end == start) to match backend (contracts.py end>=start) + ContractCreate R203.
      if (formData.start_date && formData.end_date && formData.start_date > formData.end_date) {
        errors.end_date = 'วันที่สิ้นสุดต้องไม่น้อยกว่าวันที่เริ่มต้น';
      }
    }

    // ตรวจสอบเหตุผลการแก้ไขสำหรับสัญญา ACTIVE
    if (isActiveContract && !formData.amendment_reason?.trim()) {
      errors.amendment_reason = 'กรุณากรอกเหตุผลในการแก้ไขสัญญา';
    }

    if (availableSigners.length > 0 && selectedSignerIds.length < minRequiredSigners) {
      errors.signers = `กรุณาเลือกผู้ลงนามอย่างน้อย ${minRequiredSigners} คน`;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // บันทึกข้อมูล
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form and get errors
    const validationErrors: Record<string, string> = {};

    if (!formData.contract_number.trim()) {
      validationErrors.contract_number = 'กรุณากรอกเลขที่สัญญา';
    }
    if (!formData.company_id) {
      validationErrors.company_id = 'กรุณาเลือกบริษัท';
    }
    if (isMain) {
      // FB-2026-0004: สัญญาหลักไม่มีวันเริ่ม/วันสิ้นสุด
      if (!formData.contract_date) {
        validationErrors.contract_date = 'กรุณาระบุวันที่ทำสัญญา';
      }
    } else {
      if (!formData.start_date) {
        validationErrors.start_date = 'กรุณาเลือกวันที่เริ่มต้นสัญญา';
      }
      if (!formData.end_date) {
        validationErrors.end_date = 'กรุณาเลือกวันที่สิ้นสุดสัญญา';
      }
      // R204: allow same-day (end == start) — parity with backend + ContractCreate (R203).
      if (formData.start_date && formData.end_date && formData.start_date > formData.end_date) {
        validationErrors.end_date = 'วันที่สิ้นสุดต้องไม่น้อยกว่าวันที่เริ่มต้น';
      }
    }
    // ตรวจสอบเหตุผลการแก้ไขสำหรับสัญญา ACTIVE
    if (isActiveContract && !formData.amendment_reason?.trim()) {
      validationErrors.amendment_reason = 'กรุณากรอกเหตุผลในการแก้ไขสัญญา';
    }

    if (availableSigners.length > 0 && selectedSignerIds.length < minRequiredSigners) {
      validationErrors.signers = `กรุณาเลือกผู้ลงนามอย่างน้อย ${minRequiredSigners} คน`;
    }

    // Set form errors state
    setFormErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      // Scroll to first error and focus
      const firstErrorField = Object.keys(validationErrors)[0];
      if (firstErrorField) {
        // Use setTimeout to ensure DOM is updated after state change
        setTimeout(() => {
          const element = document.querySelector(`[name="${firstErrorField}"]`) ||
                         document.querySelector(`#${firstErrorField}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            (element as HTMLElement).focus?.();
          }
        }, 100);
      }
      return;
    }

    try {
      setSubmitting(true);
      
      // เตรียมข้อมูลสำหรับส่ง API
      // SM8: `status` ไม่ใช่ field ของฟอร์มอีกต่อไป — transition ทำผ่านปุ่ม อนุมัติ/ปฏิเสธ/ยกเลิกสัญญา
      // เท่านั้น (PUT /contracts/{id} ตอบ 422 ถ้าส่ง status ต่างจากค่าปัจจุบัน · create ให้ backend
      // default เป็น DRAFT) จึงต้องตัดออกจาก payload ก่อนส่ง
      const { status: _status, contract_date: _contractDate, start_date: _start, end_date: _end, ...editableData } = formData;
      // FB-2026-0004: สัญญาหลัก — ส่งวันที่ทำสัญญา ห้ามส่ง end_date/rental_ids (backend 422/400)
      //   รายการเช่าผูกผ่านสัญญาแนบท้ายเท่านั้น · สัญญาแบบเดิมส่งวันเริ่ม/สิ้นสุด + rental_ids เหมือนเดิม
      const submitData: any = isMain
        ? {
            ...editableData,
            company_id: Number(formData.company_id),
            contract_date: formData.contract_date,
            signer_ids: selectedSignerIds.length > 0 ? selectedSignerIds : undefined
          }
        : {
            ...editableData,
            start_date: formData.start_date,
            end_date: formData.end_date,
            company_id: Number(formData.company_id),
            rental_ids: selectedRentalIds, // เพิ่มรายการเช่าที่เลือก
            signer_ids: selectedSignerIds.length > 0 ? selectedSignerIds : undefined
          };

      let contractId: number;
      
      if (id) {
        if (recordId === null) return; // BUG-S2283-001: id ไม่ถูกต้อง — ไม่ยิง PUT /contracts/NaN
        // S1230: opt-in optimistic-concurrency via If-Match (OBS-S1223-001 stack)
        await contractService.updateContract(recordId, submitData, contractUpdatedAt);
        contractId = recordId;
        
        // อัพโหลดไฟล์ที่แนบ (ถ้ามี)
        if (attachedFiles.length > 0) {
          try {
            for (const file of attachedFiles) {
              await contractService.uploadContractDocument(contractId, file);
            }
            showAlert('สำเร็จ', `แก้ไขสัญญาและอัพโหลดไฟล์แนบ ${attachedFiles.length} ไฟล์เรียบร้อยแล้ว`, 'success');
          } catch (uploadError) {
            showAlert('แก้ไขสัญญาสำเร็จ แต่มีปัญหาในการอัพโหลดไฟล์', 
                     'สัญญาถูกแก้ไขแล้ว แต่ไม่สามารถอัพโหลดไฟล์แนบได้', 
                     'warning');
          }
        } else {
          const message = isActiveContract 
            ? 'แก้ไขสัญญาเรียบร้อยแล้ว (บันทึกเหตุผลการแก้ไขแล้ว)' 
            : 'แก้ไขสัญญาเรียบร้อยแล้ว';
          showAlert('สำเร็จ', message, 'success');
        }
      } else {
        const response = await contractService.createContract(submitData);
        contractId = response.id;
        
        // อัพโหลดไฟล์ที่แนบ (ถ้ามี)
        if (attachedFiles.length > 0) {
          try {
            for (const file of attachedFiles) {
              await contractService.uploadContractDocument(contractId, file);
            }
            showAlert('สำเร็จ', `สร้างสัญญาใหม่และอัพโหลดไฟล์แนบ ${attachedFiles.length} ไฟล์เรียบร้อยแล้ว`, 'success');
          } catch (uploadError) {
            showAlert('สร้างสัญญาสำเร็จ แต่มีปัญหาในการอัพโหลดไฟล์', 
                     'สัญญาถูกสร้างแล้ว แต่ไม่สามารถอัพโหลดไฟล์แนบได้', 
                     'warning');
          }
        } else {
          showAlert('สำเร็จ', 'สร้างสัญญาใหม่เรียบร้อยแล้ว', 'success');
        }
      }

      setIsFormDirty(false); // ปิดการเตือนก่อน navigate
      navigate('/contracts');
    } catch (error: any) {
      console.error('Error saving contract:', error);

      // S1230: dedicated handling for 412 Precondition Failed (stale write)
      if (error.response?.status === 412) {
        const staleMsg = error.response?.data?.error?.user_message
          || 'ข้อมูลถูกแก้ไขโดยผู้ใช้อื่น กรุณาโหลดข้อมูลใหม่แล้วลองอีกครั้ง';
        showAlert('ข้อมูลล้าสมัย', staleMsg, 'warning');
        // โหลดข้อมูลใหม่อัตโนมัติเพื่อให้ user เห็น state ปัจจุบัน
        await loadContractData();
        setSubmitting(false);
        return;
      }

      // R169 fix (fe_error_envelope_mismatch): the backend envelope is
      // {error:{message,user_message}} — it never returns `.detail`, so the old
      // `.detail`-only read always fell through to the generic message and the
      // user never saw the real reason. Read the current-envelope paths first.
      // BUG-S2084-002: ใช้ตัวกลาง — รู้จักกรณีเน็ตหลุด/timeout ด้วย (เดิมได้ข้อความ generic)
      showAlert('เกิดข้อผิดพลาด', getApiErrorMessage(error, 'เกิดข้อผิดพลาดในการบันทึกข้อมูล'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // คำนวณจำนวนวัน
  const calculateDays = (): number => {
    if (!formData.start_date || !formData.end_date) return 0;
    
    const start = new Date(formData.start_date);
    const end = new Date(formData.end_date);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  // จัดการการเลือกไฟล์
  const handleFileSelect = (files: File[]) => {
    const validFiles = files.filter(file => {
      // ตรวจสอบประเภทไฟล์
      const allowedTypes = ['application/pdf', 'application/msword', 
                           'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                           'image/jpeg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        showAlert('ประเภทไฟล์ไม่ถูกต้อง', `ไฟล์ ${file.name} ไม่ใช่ประเภทที่รองรับ`, 'error');
        return false;
      }
      
      // ตรวจสอบขนาดไฟล์ (10MB)
      if (file.size > 10 * 1024 * 1024) {
        showAlert('ไฟล์ใหญ่เกินไป', `ไฟล์ ${file.name} มีขนาดเกิน 10MB`, 'error');
        return false;
      }
      
      return true;
    });
    
    setAttachedFiles(prev => [...prev, ...validFiles]);
  };

  // ลบไฟล์ที่เลือก
  const removeFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // โหลดเอกสารที่มีอยู่ของสัญญา
  const loadExistingDocuments = async (contractId: number) => {
    try {
      setLoadingDocuments(true);
      // ใช้ documents API เพื่อดึงเอกสารที่เกี่ยวข้องกับสัญญา
      const response = await ApiWithLogging.get(`/documents/contract/${contractId}`);
      
      // ตรวจสอบว่าเป็น array หรือไม่
      if (Array.isArray(response.data)) {
        setExistingDocuments(response.data);
      } else {
        setExistingDocuments([]);
      }
    } catch (error: any) {
      // ไม่ต้องแสดง error เมื่อไม่พบเอกสาร (404) เพราะอาจจะยังไม่มีเอกสารจริง ๆ
      if (error.response?.status === 404) {
        setExistingDocuments([]);
      } else {
        console.error('Error loading existing documents:', error);
      }
    } finally {
      setLoadingDocuments(false);
    }
  };

  // ลบเอกสารที่มีอยู่
  const deleteExistingDocument = async (documentId: number) => {
    try {
      const confirmed = await showConfirm(
        'ยืนยันการลบ',
        'คุณต้องการลบเอกสารนี้หรือไม่?'
      );

      if (confirmed) {
        await ApiWithLogging.delete(`/documents/${documentId}`);
        
        showAlert('สำเร็จ', 'ลบเอกสารเรียบร้อยแล้ว', 'success');
        // รีโหลดรายการเอกสาร
        if (recordId !== null) {
          await loadExistingDocuments(recordId);
        }
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      showAlert('เกิดข้อผิดพลาด', 'ไม่สามารถลบเอกสารได้', 'error');
    }
  };

  // ดาวน์โหลดเอกสาร
  const downloadDocument = async (doc: any) => {
    try {
      setDownloadingDocId(doc.id);
      const response = await ApiWithLogging.get(`/documents/${doc.id}/download`, {
        responseType: 'blob'
      });

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename || doc.name || 'document';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      showAlert('เกิดข้อผิดพลาด', await getApiErrorMessageAsync(error, 'ไม่สามารถดาวน์โหลดเอกสารได้'), 'error');
    } finally {
      setDownloadingDocId(null);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-secondary-600">กำลังโหลดข้อมูล...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <ConfirmModal
        isOpen={showModal}
        onClose={onCancelLeave}
        onConfirm={onConfirmLeave}
        pushHistoryOnOpen={false}
        title="ข้อมูลยังไม่ได้บันทึก"
        message="คุณมีข้อมูลที่ยังไม่ได้บันทึก หากออกจากหน้านี้ข้อมูลที่กรอกจะหายไป ต้องการออกจากหน้านี้หรือไม่?"
        confirmText="ออกจากหน้านี้"
        cancelText="อยู่ต่อ"
        isDanger={true}
      />
      <ConfirmModal
        isOpen={showBackConfirm}
        onClose={() => setShowBackConfirm(false)}
        onConfirm={() => { setShowBackConfirm(false); setIsFormDirty(false); navigate('/contracts'); }}
        title="ข้อมูลยังไม่ได้บันทึก"
        message="คุณมีข้อมูลที่ยังไม่ได้บันทึก หากออกจากหน้านี้ข้อมูลที่กรอกจะหายไป ต้องการออกจากหน้านี้หรือไม่?"
        confirmText="ออกจากหน้านี้"
        cancelText="อยู่ต่อ"
        isDanger={true}
      />
      {/* FB-2026-0006: สัญญาแนบท้ายแก้ผ่านโมดัลบันทึกส่ง-มอบ (เฉพาะสถานะร่าง) */}
      {contractKind === 'ADDENDUM' && (
        <div className="max-w-3xl mx-auto mb-6 rounded-lg border border-info-200 bg-info-50 p-4" data-testid="addendum-edit-notice">
          <p className="text-sm text-info-800">
            สัญญานี้เป็น "สัญญาแนบท้าย" (บันทึกส่ง-มอบ) — แก้ไขรายละเอียดผ่านแบบฟอร์มบันทึกส่ง-มอบ
            {formData.status !== 'DRAFT' && ' (แก้ไขได้เฉพาะสถานะร่าง)'}
          </p>
          <div className="mt-3 flex gap-2">
            {formData.status === 'DRAFT' && addendumForModal && (
              <button type="button" onClick={() => setShowAddendumModal(true)}
                className="px-4 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700">
                เปิดแบบฟอร์มแก้ไขสัญญาแนบท้าย
              </button>
            )}
            <button type="button" onClick={() => navigate(`/contracts/view/${recordId}`)}
              className="px-4 py-2 rounded-lg border border-secondary-300 text-secondary-700 hover:bg-secondary-50">
              กลับไปหน้ารายละเอียด
            </button>
          </div>
          <AddendumFormModal
            isOpen={showAddendumModal}
            onClose={() => setShowAddendumModal(false)}
            addendum={addendumForModal}
            onSaved={() => { setShowAddendumModal(false); navigate(`/contracts/view/${recordId}`); }}
          />
        </div>
      )}
      {contractKind !== 'ADDENDUM' && (
      <div className="w-full">
        {/* Header with Gradient Navy Theme */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-primary-600 flex items-center justify-center flex-shrink-0 shadow-sm">
            <RiEditLine className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-secondary-900 leading-tight">แก้ไขสัญญา</h1>
            <p className="text-xs text-secondary-500 mt-0.5">แก้ไขและปรับปรุงข้อมูลสัญญาเช่า</p>
          </div>
        </div>
      </div>

        {/* Form Container */}
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg border border-secondary-200">
            <div className="px-6 py-6">
              <div className="flex items-center gap-2 pb-2 border-b border-secondary-200 mb-6">
                <div className="w-8 h-8 bg-primary-600 rounded-md flex items-center justify-center shadow-sm">
                  <RiFileTextLine className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-base font-semibold text-secondary-800">
                  {id ? 'แก้ไขข้อมูลสัญญา' : 'สร้างสัญญาใหม่'}
                </h3>
              </div>

              {/* แจ้งเตือนสำหรับสัญญา ACTIVE */}
              {isActiveContract && (
                <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start">
                    <RiInformationLine className="text-amber-500 mr-2 mt-0.5" size={16} />
                    <div>
                      <h4 className="text-sm font-medium text-amber-800">การแก้ไขสัญญาที่มีผลบังคับใช้</h4>
                      <p className="text-sm text-amber-700 mt-1">
                        สัญญานี้มีสถานะ "มีผลบังคับใช้" การแก้ไขจะถูกบันทึกเป็นประวัติ กรุณาระบุเหตุผลในการแก้ไขในช่องที่กำหนด
                      </p>
                    </div>
                  </div>
                </div>
              )}
              <form onSubmit={handleSubmit} onChange={() => setIsFormDirty(true)} noValidate className="space-y-6">
                {/* ข้อมูลทั่วไป */}
                <div className="border-b border-secondary-200 pb-6">
                  <div className="flex items-center gap-2 pb-2 border-b border-secondary-200 mb-4">
                    <div className="w-8 h-8 bg-primary-600 rounded-md flex items-center justify-center shadow-sm">
                      <RiInformationLine className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-base font-semibold text-secondary-800">ข้อมูลทั่วไป</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="contract-edit-number" className="block text-sm font-medium text-secondary-700 mb-1">
                        เลขที่สัญญา *
                      </label>
                      <input
                        id="contract-edit-number"
                        type="text"
                        name="contract_number"
                        value={formData.contract_number}
                        onChange={handleFormChange}
                        className={`input ${formErrors.contract_number ? 'border-danger-500' : ''} ${submitting || id ? 'bg-secondary-50 cursor-not-allowed' : ''}`}
                        placeholder="กรอกเลขที่สัญญา"
                        disabled={submitting}
                        readOnly={!!id}
                        aria-invalid={Boolean(formErrors.contract_number)}
                        aria-describedby={formErrors.contract_number ? 'contract_number-error' : undefined}
                      />
                      {formErrors.contract_number && (
                        <p id="contract_number-error" className="mt-1 text-sm text-danger-600">{formErrors.contract_number}</p>
                      )}
                    </div>

                    <div>
                      <span className="block text-sm font-medium text-secondary-700 mb-1">
                        สถานะ
                      </span>
                      <div className="flex items-center min-h-[38px]">
                        <span
                          data-testid="contract-edit-status-badge"
                          className={`px-2.5 py-1 inline-flex items-center gap-1.5 text-xs font-semibold rounded-full border ${statusBadge.bg} ${statusBadge.text} ${statusBadge.border}`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${statusBadge.dot}`} />
                          {statusBadge.label}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-secondary-500">
                        {id
                          ? 'เปลี่ยนสถานะได้จากปุ่ม อนุมัติสัญญา / ปฏิเสธสัญญา / ยกเลิกสัญญา ในเมนูจัดการของหน้ารายการสัญญา'
                          : 'สัญญาใหม่จะถูกสร้างเป็นร่างสัญญา อนุมัติแล้วจึงมีผลบังคับใช้'}
                      </p>
                    </div>

                  </div>
                </div>

                {/* ข้อมูลบริษัท */}
                <div className="border-b border-secondary-200 pb-6">
                  <div className="flex items-center gap-2 pb-2 border-b border-secondary-200 mb-4">
                    <div className="w-8 h-8 bg-info-600 rounded-md flex items-center justify-center shadow-sm">
                      <RiBuildingLine className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-base font-semibold text-secondary-800">ข้อมูลบริษัท</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        บริษัท *
                      </label>
                      <SearchableSelect
                        options={companies.map(company => ({
                          value: company.id,
                          label: company.name
                        }))}
                        value={formData.company_id}
                        onChange={(value) => handleFormChange({ target: { name: 'company_id', value } } as any)}
                        placeholder="-- เลือกบริษัท --"
                        searchPlaceholder="ค้นหาบริษัท..."
                        disabled={submitting}
                        error={formErrors.company_id}
                      />
                    </div>
                  </div>

                  {/* ผู้มีอำนาจลงนาม */}
                  {formData.company_id && (availableSigners.length > 0 || existingSignerSnapshots.length > 0) && (
                    <div className="mt-4">
                      <label id="signers-label" className="block text-sm font-medium text-secondary-700 mb-2">
                        ผู้มีอำนาจลงนาม <span className="text-danger-500">*</span>
                        {minRequiredSigners > 1 && (
                          <span className="text-xs text-secondary-500 ml-2">
                            (ต้องเลือกอย่างน้อย {minRequiredSigners} คน)
                          </span>
                        )}
                      </label>
                      {/* BUG-S2085-004: กลุ่ม checkbox ต้องมีชื่อกลุ่ม + ผูกข้อความ error ให้ screen reader อ่านตอนโฟกัส */}
                      <div
                        role="group"
                        aria-labelledby="signers-label"
                        aria-describedby={formErrors.signers ? 'signers-error' : undefined}
                        className="space-y-2 border border-secondary-200 rounded-lg p-3"
                      >
                        {availableSigners.map(signer => (
                          <label key={signer.id} className="flex items-center gap-3 p-2 rounded hover:bg-secondary-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedSignerIds.includes(signer.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedSignerIds(prev => [...prev, signer.id]);
                                } else {
                                  setSelectedSignerIds(prev => prev.filter(sid => sid !== signer.id));
                                }
                                if (formErrors.signers) {
                                  setFormErrors(prev => ({ ...prev, signers: '' }));
                                }
                                setIsFormDirty(true);
                              }}
                              className="rounded border-secondary-300 text-primary-600"
                              disabled={submitting}
                            />
                            <div>
                              <span className="font-medium text-secondary-900">{signer.full_name}</span>
                              <span className="text-sm text-secondary-500 ml-2">({signer.position})</span>
                              {signer.is_primary && (
                                <span className="ml-2 px-1.5 py-0.5 text-xs bg-primary-100 text-primary-700 rounded">หลัก</span>
                              )}
                            </div>
                          </label>
                        ))}
                        {/* KD3 S2122 (BUG-S2122-002): ผู้ลงนามที่ถูกเลือกไว้แต่ไม่อยู่ในรายชื่อที่ใช้งาน — เดิมมองไม่เห็นแต่ยังถูกส่งไป
                            (ถูกลบแล้ว → บันทึกไม่ได้ตลอดไป) · แสดงให้ติ๊กออกได้ */}
                        {existingSignerSnapshots
                          .filter(snap => selectedSignerIds.includes(snap.id) && !availableSigners.some(a => a.id === snap.id))
                          .map(snap => (
                            <label key={`gone-${snap.id}`} className="flex items-center gap-3 p-2 rounded bg-warning-50 cursor-pointer">
                              <input
                                type="checkbox"
                                checked
                                onChange={() => {
                                  setSelectedSignerIds(prev => prev.filter(sid => sid !== snap.id));
                                  setIsFormDirty(true);
                                }}
                                className="rounded border-secondary-300 text-primary-600"
                                disabled={submitting}
                              />
                              <div>
                                <span className="font-medium text-secondary-900">{snap.name}</span>
                                <span className="text-sm text-secondary-500 ml-2">({snap.position})</span>
                                <span className="ml-2 text-xs text-warning-700">ไม่อยู่ในรายชื่อผู้ลงนามที่ใช้งานแล้ว — เอาเครื่องหมายออกแล้วเลือกคนอื่นแทน</span>
                              </div>
                            </label>
                          ))}
                      </div>
                      {formErrors.signers && (
                        <p id="signers-error" className="mt-1 text-sm text-danger-600">{formErrors.signers}</p>
                      )}
                    </div>
                  )}
                </div>

                {/* ระยะเวลาและเงื่อนไข */}
                <div className="border-b border-secondary-200 pb-6">
                  <div className="flex items-center gap-2 pb-2 border-b border-secondary-200 mb-4">
                    <div className="w-8 h-8 bg-warning-600 rounded-md flex items-center justify-center shadow-sm">
                      <RiCalendarLine className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-base font-semibold text-secondary-800">ระยะเวลาและค่าใช้จ่าย</h3>
                  </div>

                  {isMain ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="contract-edit-contract-date" className="block text-sm font-medium text-secondary-700 mb-1">
                        วันที่ทำสัญญา *
                      </label>
                      <input
                        id="contract-edit-contract-date"
                        type="date"
                        name="contract_date"
                        value={formData.contract_date || ''}
                        onChange={handleFormChange}
                        className={`input ${formErrors.contract_date ? 'border-danger-500' : ''} ${submitting ? 'bg-secondary-50 cursor-not-allowed' : ''}`}
                        disabled={submitting}
                        aria-invalid={Boolean(formErrors.contract_date)}
                        aria-describedby={formErrors.contract_date ? 'contract_date-error' : undefined}
                      />
                      {formErrors.contract_date && (
                        <p id="contract_date-error" className="mt-1 text-sm text-danger-600">{formErrors.contract_date}</p>
                      )}
                    </div>
                    <div className="text-sm text-secondary-600 self-end pb-2" data-testid="open-ended-note">
                      สัญญาหลักเป็นสัญญาแบบเปิด ไม่มีวันเริ่ม/วันสิ้นสุด · อุปกรณ์ผูกผ่านสัญญาแนบท้าย (เมนู ⋮ ในหน้าจัดการสัญญา)
                    </div>
                  </div>
                  ) : (
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="contract-edit-start-date" className="block text-sm font-medium text-secondary-700 mb-1">
                        วันที่เริ่มต้น *
                      </label>
                      <input
                        id="contract-edit-start-date"
                        type="date"
                        name="start_date"
                        value={formData.start_date}
                        onChange={handleFormChange}
                        className={`input ${formErrors.start_date ? 'border-danger-500' : ''} ${submitting ? 'bg-secondary-50 cursor-not-allowed' : ''}`}
                        disabled={submitting}
                        aria-invalid={Boolean(formErrors.start_date)}
                        aria-describedby={formErrors.start_date ? 'start_date-error' : undefined}
                      />
                      {formErrors.start_date && (
                        <p id="start_date-error" className="mt-1 text-sm text-danger-600">{formErrors.start_date}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="contract-edit-end-date" className="block text-sm font-medium text-secondary-700 mb-1">
                        วันที่สิ้นสุด *
                      </label>
                      <input
                        id="contract-edit-end-date"
                        type="date"
                        name="end_date"
                        value={formData.end_date}
                        onChange={handleFormChange}
                        className={`input ${formErrors.end_date ? 'border-danger-500' : ''} ${submitting ? 'bg-secondary-50 cursor-not-allowed' : ''}`}
                        disabled={submitting}
                        aria-invalid={Boolean(formErrors.end_date)}
                        aria-describedby={formErrors.end_date ? 'end_date-error' : undefined}
                      />
                      {formErrors.end_date && (
                        <p id="end_date-error" className="mt-1 text-sm text-danger-600">{formErrors.end_date}</p>
                      )}
                    </div>

                  </div>
                  )}

                </div>

                {/* ข้อมูลเพิ่มเติม */}
                <div>
                  <div className="flex items-center gap-2 pb-2 border-b border-secondary-200 mb-4">
                    <div className="w-8 h-8 bg-info-600 rounded-md flex items-center justify-center shadow-sm">
                      <RiFileTextLine className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-base font-semibold text-secondary-800">ข้อมูลเพิ่มเติม</h3>
                  </div>
                  
                  <div className="space-y-4">

                    <div>
                      <label htmlFor="contract-edit-terms" className="block text-sm font-medium text-secondary-700 mb-1">
                        ข้อกำหนดและเงื่อนไขเพิ่มเติม
                      </label>
                      <textarea
                        id="contract-edit-terms"
                        name="terms_conditions"
                        value={formData.terms_conditions}
                        onChange={handleFormChange}
                        rows={4}
                        className={`input ${formErrors.terms_conditions ? 'border-danger-500' : ''} ${submitting ? 'bg-secondary-50 cursor-not-allowed' : ''}`}
                        placeholder="ระบุข้อกำหนดและเงื่อนไขเพิ่มเติม (ถ้ามี)"
                        disabled={submitting}
                      />
                    </div>

                    <div>
                      <label htmlFor="contract-edit-notes" className="block text-sm font-medium text-secondary-700 mb-1">
                        หมายเหตุ
                      </label>
                      <textarea
                        id="contract-edit-notes"
                        name="notes"
                        value={formData.notes}
                        onChange={handleFormChange}
                        rows={3}
                        className={`input ${formErrors.notes ? 'border-danger-500' : ''} ${submitting ? 'bg-secondary-50 cursor-not-allowed' : ''}`}
                        placeholder="หมายเหตุเพิ่มเติม..."
                        disabled={submitting}
                      />
                    </div>

                    {/* ฟิลด์เหตุผลการแก้ไข (แสดงเฉพาะสัญญา ACTIVE) */}
                    {isActiveContract && (
                      <div>
                        <label htmlFor="amendment_reason" className="block text-sm font-medium text-secondary-700 mb-1">
                          เหตุผลในการแก้ไขสัญญา *
                        </label>
                        <div className="relative">
                          <textarea
                            id="amendment_reason"
                            name="amendment_reason"
                            value={formData.amendment_reason || ''}
                            onChange={handleFormChange}
                            rows={3}
                            className={`input ${formErrors.amendment_reason ? 'border-danger-500 bg-danger-50' : ''} ${submitting ? 'bg-secondary-50 cursor-not-allowed' : ''}`}
                            placeholder="ระบุเหตุผลในการแก้ไขสัญญานี้..."
                            disabled={submitting}
                            aria-invalid={Boolean(formErrors.amendment_reason)}
                            aria-describedby={formErrors.amendment_reason ? 'amendment_reason-error' : undefined}
                          />
                          {formErrors.amendment_reason && (
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-start pt-3 pointer-events-none">
                              <svg className="h-5 w-5 text-danger-500" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </div>
                        {formErrors.amendment_reason && (
                          <div id="amendment_reason-error" className="mt-1 flex items-center">
                            <svg className="h-4 w-4 text-danger-500 mr-1" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <p className="text-sm text-danger-600 font-medium">{formErrors.amendment_reason}</p>
                          </div>
                        )}
                        {!formErrors.amendment_reason && (
                          <p className="mt-1 text-sm text-secondary-500">
                            เนื่องจากสัญญานี้มีสถานะเป็น "มีผลบังคับใช้" กรุณาระบุเหตุผลในการแก้ไข
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* รายการเช่าที่สามารถเพิ่มในสัญญา — สัญญาหลัก (FB-2026-0004) ผูกผ่านสัญญาแนบท้ายแทน */}
                {formData.company_id && !isMain && (
                  <div className="border-b border-secondary-200 pb-6">
                    <div className="flex items-center gap-2 pb-2 border-b border-secondary-200 mb-4">
                      <div className="w-8 h-8 bg-success-600 rounded-md flex items-center justify-center shadow-sm">
                        <RiListCheck className="w-4 h-4 text-white" />
                      </div>
                      <h3 className="text-base font-semibold text-secondary-800">เลือกรายการเช่าที่ต้องการเพิ่มในสัญญา</h3>
                    </div>

                    <div className="bg-secondary-50 p-3 rounded-lg mb-4">
                      <p className="text-sm text-secondary-600">
                        เลือกรายการเช่าที่ต้องการรวมในสัญญานี้ ({selectedRentalIds.length} รายการที่เลือก)
                      </p>
                    </div>

                    {loadingRentals ? (
                      <div className="text-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-info-600 mx-auto"></div>
                        <p className="mt-2 text-sm text-secondary-600">กำลังโหลดรายการเช่า...</p>
                      </div>
                    ) : companyRentals.length > 0 ? (
                      <div className="border border-secondary-200 rounded-lg max-h-96 overflow-y-auto overflow-x-auto">
                        <table className="min-w-full divide-y divide-secondary-200">
                          <thead className="bg-secondary-50 sticky top-0 border-b-2 border-secondary-200">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-600">
                                เลือก
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-600">
                                เลขที่การเช่า
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-600">
                                สินค้า
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-600">
                                อุปกรณ์
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-600">
                                ค่าเช่า/เดือน
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-600">
                                สถานะ
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-secondary-200">
                            {companyRentals.map((rental) => {
                              const isSelected = selectedRentalIds.includes(rental.id);
                              const isInCurrentContract = recordId !== null && rental.contract_id === recordId;
                              const isDisabled = submitting;

                              return (
                                <tr key={rental.id} className={isSelected ? 'bg-info-50' : ''}>
                                  <td className="px-4 py-4 whitespace-nowrap">
                                    <input
                                      type="checkbox"
                                      aria-label={`เลือกรายการเช่า ${rental.code || rental.rental_number || rental.id}`}
                                      checked={isSelected}
                                      onChange={() => handleRentalToggle(rental.id)}
                                      disabled={isDisabled}
                                      className={`h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded ${
                                        isDisabled ? 'cursor-not-allowed opacity-50' : ''
                                      }`}
                                    />
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap text-sm text-secondary-900">
                                    {rental.code || rental.rental_number || '-'}
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap text-sm text-secondary-900">
                                    {rental.product_name || rental.product?.name || '-'}
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap text-sm text-secondary-600">
                                    {rental.device_name || rental.device?.serial_number || '-'}
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap text-sm text-secondary-600">
                                    {rental.monthly_fee?.toLocaleString() || '-'} บาท
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap text-sm">
                                    {isInCurrentContract ? (
                                      <span className="text-accent-700 font-medium">อยู่ในสัญญานี้</span>
                                    ) : isSelected ? (
                                      <span className="text-success-700">เลือกแล้ว</span>
                                    ) : (
                                      <span className="text-info-700">พร้อมเพิ่มในสัญญา</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-4 text-secondary-500">
                        {formData.company_id 
                          ? 'ไม่มีรายการเช่าสำหรับบริษัทนี้' 
                          : 'กรุณาเลือกบริษัทก่อน'}
                      </div>
                    )}
                  </div>
                )}

                {/* แนบเอกสาร */}
                <div className="border-b border-secondary-200 pb-6">
                  <div className="flex items-center gap-2 pb-2 border-b border-secondary-200 mb-4">
                    <div className="w-8 h-8 bg-info-600 rounded-md flex items-center justify-center shadow-sm">
                      <RiAttachmentLine className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-base font-semibold text-secondary-800">แนบเอกสาร (ไม่บังคับ)</h3>
                  </div>
                  
                  {/* เอกสารที่มีอยู่ */}
                  {loadingDocuments ? (
                    <div className="mb-4 text-center py-4">
                      <div className="inline-flex items-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600 mr-2"></div>
                        <span className="text-sm text-secondary-600">กำลังโหลดเอกสาร...</span>
                      </div>
                    </div>
                  ) : existingDocuments.length > 0 ? (
                    <div className="mb-4">
                      <h5 className="text-sm font-medium text-secondary-700 mb-2">เอกสารที่มีอยู่:</h5>
                      <div className="space-y-2">
                        {existingDocuments.map((doc, index) => (
                          <div key={doc.id || index} className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <RiAttachmentLine className="text-secondary-400" />
                              <div>
                                <p className="text-sm font-medium text-secondary-700">{doc.filename || doc.name}</p>
                                <p className="text-xs text-secondary-500">
                                  {doc.file_size && `${(doc.file_size / 1024 / 1024).toFixed(2)} MB`}
                                  {doc.created_at && ` • อัพโหลดเมื่อ ${new Date(doc.created_at).toLocaleDateString('th-TH')}`}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => downloadDocument(doc)}
                                className="text-primary-600 hover:text-primary-700 text-sm"
                              >
                                ดาวน์โหลด
                              </button>
                              <button
                                type="button"
                                onClick={() => deleteExistingDocument(doc.id)}
                                className="text-danger-600 hover:text-danger-700 text-sm"
                              >
                                ลบ
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  
                  {/* แนบเอกสารใหม่ */}
                  <h5 className="text-sm font-medium text-secondary-700 mb-2 mt-4">แนบเอกสารใหม่:</h5>
                  
                  {/* Drag & Drop Zone */}
                  <div
                    onDragOver={(e) => {
                      e.preventDefault();
                      setIsDragOver(true);
                    }}
                    onDragLeave={() => setIsDragOver(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setIsDragOver(false);
                      const files = Array.from(e.dataTransfer.files);
                      handleFileSelect(files);
                    }}
                    className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                      isDragOver 
                        ? 'border-primary-500 bg-primary-50' 
                        : 'border-secondary-300 hover:border-secondary-400'
                    }`}
                  >
                    <RiUploadLine className="mx-auto h-12 w-12 text-secondary-400" />
                    <p className="mt-2 text-sm text-secondary-600">
                      ลากและวางไฟล์ที่นี่ หรือ
                    </p>
                    {/* sr-only (ไม่ใช่ hidden) — display:none ทำให้ Tab ข้ามช่องนี้ แนบไฟล์ด้วยคีย์บอร์ดไม่ได้ (BUG-S2085-001) */}
                    <label className="mt-2 inline-block cursor-pointer rounded-md focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500">
                      <span className="text-primary-600 hover:text-primary-700 font-medium">
                        คลิกเพื่อเลือกไฟล์
                      </span>
                      <input
                        type="file"
                        multiple
                        aria-label="เลือกไฟล์แนบสัญญา"
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          handleFileSelect(files);
                        }}
                        className="sr-only"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      />
                    </label>
                    <p className="text-xs text-secondary-500 mt-2">
                      รองรับไฟล์: PDF, DOC, DOCX, JPG, PNG (ขนาดไม่เกิน 10MB)
                    </p>
                  </div>
                  
                  {/* รายการไฟล์ที่แนบ */}
                  {attachedFiles.length > 0 && (
                    <div className="mt-4">
                      <h5 className="text-sm font-medium text-secondary-700 mb-2">
                        ไฟล์ที่เลือก ({attachedFiles.length} ไฟล์)
                      </h5>
                      <div className="space-y-2">
                        {attachedFiles.map((file, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg"
                          >
                            <div className="flex items-center">
                              <RiAttachmentLine className="text-secondary-400 mr-2" />
                              <span className="text-sm text-secondary-900">{file.name}</span>
                              <span className="ml-2 text-xs text-secondary-500">
                                ({(file.size / 1024 / 1024).toFixed(2)} MB)
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeFile(index)}
                              aria-label={`นำไฟล์ ${file.name} ออก`}
                              title="นำไฟล์ออก"
                              className="text-danger-600 hover:text-danger-700"
                            >
                              <RiDeleteBinLine className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Form Actions */}
                <div className="flex flex-wrap justify-end gap-3 pt-6">
                  <button
                    type="button"
                    onClick={handleBack}
                    className="group px-4 min-h-[44px] sm:min-h-[38px] text-secondary-600 border border-secondary-300 rounded-lg hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                    disabled={submitting}
                  >
                    ยกเลิก
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="group px-6 min-h-[44px] sm:min-h-[38px] bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 transition-all shadow-sm flex items-center"
                  >
                    {submitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>กำลังบันทึก...</span>
                      </>
                    ) : (
                      <>
                        <RiSaveLine className="mr-2 group-hover:scale-110 transition-transform duration-300" />
                        <span>{id ? 'บันทึกการแก้ไข' : 'สร้างสัญญา'}</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Loading Overlay สำหรับดาวน์โหลดเอกสาร */}
      <LoadingOverlayModal
        isOpen={downloadingDocId !== null}
        icon="download"
        title="กำลังดาวน์โหลดเอกสาร"
        submessage="กรุณารอสักครู่..."
      />
    </Layout>
  );
};

export default ContractEditPage;